import { Input, VStack } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import * as gqlTypesExp from "@hydrologiq/gql-experience/graphql"

import { useEffect, useState } from "react"
import { useMutation } from "@apollo/client"
import Form from "@hydrologiq/shared/chakra/custom/Form"
import ManageLocation from "@hydrologiq/shared/asset/location/ManageLocation"
import { useAppSelector } from "@hydrologiq/shared/state/hooks"
import { getLocations } from "@hydrologiq/shared/state/slices/locationsSlice"

import { IANATimeZone } from "@hydrologiq/common/helpers/date"
import { mapsApiKey } from "../../config"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import { CreateSiteButton, EditButton } from "@hydrologiq/shared/content/components/Button"
import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import { showSuccessToast, showErrorToast, showLoadingToast, dismissToast } from "../../utils/toast"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import {
  composeFormHelperText,
  composePlaceholder,
  composeCreatedTitle,
  composeCreatedDescription,
  composeUpdatedTitle,
  composeUpdatedDescription,
  composeValidationMessage,
  composeCreatingToast,
} from "@hydrologiq/shared/copy/utils"

type CustomerSiteCreateForm = {
  name: string
  location?: string
}

const CustomerSiteCreate = (props: {
  customerId: string
  onNewSite?: (site?: gqlTypesExp.CustomerSite) => void
  existingSite?: gqlTypesExp.CustomerSite
}) => {
  const { customerId, onNewSite, existingSite } = props
  const update = existingSite !== undefined
  const defaultFormData: CustomerSiteCreateForm = {
    name: existingSite?.name ?? "",
    location: existingSite?.location?.id ?? undefined,
  }

  const [createCustomerSite, { loading: createLoading }] = useMutation(gqlTypes.CreateCustomerSiteDocument)
  const [updateCustomerSite, { loading: updateLoading }] = useMutation(gqlTypes.UpdateCustomerSiteDocument)
  const loading = createLoading || updateLoading
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const copy = useCopyValues({
    [COPY_KEYS.BUTTONS.CREATE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.BUTTONS.CREATE],
    [COPY_KEYS.HEADINGS.SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.SITE],
    [COPY_KEYS.MESSAGES.CREATED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.CREATED],
    [COPY_KEYS.MESSAGES.UPDATED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.UPDATED],
    [COPY_KEYS.MESSAGES.SUCCESSFULLY_CREATED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.SUCCESSFULLY_CREATED],
    [COPY_KEYS.MESSAGES.SUCCESSFULLY_UPDATED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.SUCCESSFULLY_UPDATED],
    [COPY_KEYS.MESSAGES.CREATION_FAILED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.CREATION_FAILED],
    [COPY_KEYS.MESSAGES.UPDATE_FAILED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.UPDATE_FAILED],
    [COPY_KEYS.MESSAGES.VALIDATION_ERROR]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.VALIDATION_ERROR],
    [COPY_KEYS.MESSAGES.UNEXPECTED_ERROR]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.UNEXPECTED_ERROR],
    [COPY_KEYS.MESSAGES.PLEASE_PROVIDE_NAME]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.PLEASE_PROVIDE_NAME],
    [COPY_KEYS.MESSAGES.UPDATING_IN_SYSTEM]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.UPDATING_IN_SYSTEM],
    [COPY_KEYS.MESSAGES.ADDING_TO_SYSTEM]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.ADDING_TO_SYSTEM],
    [COPY_KEYS.FORMS.HELPER_TEXT_CREATE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.HELPER_TEXT_CREATE],
    [COPY_KEYS.FORMS.PLACEHOLDER_ENTER_NAME]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.PLACEHOLDER_ENTER_NAME],
    [COPY_KEYS.LABELS.NAME]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.LABELS.NAME],
    [COPY_KEYS.ACTIONS.CREATING]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.ACTIONS.CREATING],
    [COPY_KEYS.ACTIONS.UPDATING]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.ACTIONS.UPDATING],
    [COPY_KEYS.COMMON.A]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.COMMON.A],
  })

  const InternalForm = () => {
    const locations = useAppSelector(getLocations)

    useEffect(() => {
      if (!open) {
        setFormData(defaultFormData)
      }
    }, [open])

    const [formData, setFormData] = useState<CustomerSiteCreateForm>(defaultFormData)
    return (
      <Form
        helperText={composeFormHelperText(copy, COPY_KEYS.HEADINGS.SITE, update ? "edit" : "create")}
        actionType={update ? "update" : "create"}
        loading={loading}
        onSubmit={async () => {
          let loadingToastId: string | undefined
          try {
            setError(null)

            // Show loading toast
            const toastConfig = update
              ? {
                  title: `${copy[COPY_KEYS.ACTIONS.UPDATING]} ${copy[COPY_KEYS.HEADINGS.SITE]}`,
                  description: copy[COPY_KEYS.MESSAGES.UPDATING_IN_SYSTEM],
                }
              : composeCreatingToast(copy, COPY_KEYS.HEADINGS.SITE)
            loadingToastId = showLoadingToast(toastConfig.title, toastConfig.description)

            if (!formData.name || !formData.name.trim()) {
              const errorMessage = composeValidationMessage(
                copy,
                COPY_KEYS.MESSAGES.PLEASE_PROVIDE_NAME,
                COPY_KEYS.HEADINGS.SITE
              )
              setError(errorMessage)
              showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], errorMessage)
              return false
            }

            let variables = {
              customerId: customerId,
              name: formData.name.trim(),
              location: formData.location ?? "",
              timezone: IANATimeZone(),
            } as gqlTypes.CreateCustomerSiteMutationVariables & { timezone: string }

            const data = update
              ? await updateCustomerSite({ variables: { siteId: existingSite.id, ...variables } })
              : await createCustomerSite({ variables })

            if (data.data) {
              onNewSite && onNewSite()
              showSuccessToast(
                update
                  ? composeUpdatedTitle(copy, COPY_KEYS.HEADINGS.SITE)
                  : composeCreatedTitle(copy, COPY_KEYS.HEADINGS.SITE),
                update
                  ? composeUpdatedDescription(copy, COPY_KEYS.HEADINGS.SITE, formData.name)
                  : composeCreatedDescription(copy, COPY_KEYS.HEADINGS.SITE, formData.name)
              )
              setOpen(false)
              setError(null)
              return true
            } else {
              const errorMessage = `Failed to ${
                update ? copy[COPY_KEYS.MESSAGES.UPDATED].toLowerCase() : copy[COPY_KEYS.MESSAGES.CREATED].toLowerCase()
              } ${copy[COPY_KEYS.HEADINGS.SITE].toLowerCase()}. Please try again.`
              setError(errorMessage)
              showErrorToast(
                update ? copy[COPY_KEYS.MESSAGES.UPDATE_FAILED] : copy[COPY_KEYS.MESSAGES.CREATION_FAILED],
                errorMessage
              )
              return false
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : copy[COPY_KEYS.MESSAGES.UNEXPECTED_ERROR]
            setError(errorMessage)
            showErrorToast(
              update ? copy[COPY_KEYS.MESSAGES.UPDATE_FAILED] : copy[COPY_KEYS.MESSAGES.CREATION_FAILED],
              errorMessage
            )
            return false
          } finally {
            // Always dismiss loading toast
            if (loadingToastId) {
              dismissToast(loadingToastId)
            }
          }
        }}
        onCancel={() => {
          setError(null)
          setOpen(false)
        }}
      >
        <VStack gap={5}>
          {error && (
            <ErrorAlert
              title={
                update
                  ? composeUpdatedTitle(copy, COPY_KEYS.HEADINGS.SITE)
                  : composeCreatedTitle(copy, COPY_KEYS.HEADINGS.SITE)
              }
              message={error}
              onClose={() => setError(null)}
            />
          )}
          <Field label={copy[COPY_KEYS.LABELS.NAME]} required>
            <Input
              name="name"
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }}
              defaultValue={defaultFormData.name}
              placeholder={composePlaceholder(copy, COPY_KEYS.FORMS.PLACEHOLDER_ENTER_NAME, COPY_KEYS.HEADINGS.SITE)}
            />
          </Field>
          <ManageLocation
            location={formData.location}
            locations={locations}
            onUpdate={(location) => {
              setFormData((prev) => ({
                ...prev,
                location: location,
              }))
            }}
            mapsApiKey={mapsApiKey}
            newLocationDefaults={{
              name: undefined,
              lat: 54.9753147,
              long: -2.269908,
            }}
          />
        </VStack>
      </Form>
    )
  }

  return (
    <Dialog
      title={
        update
          ? `${copy[COPY_KEYS.ACTIONS.UPDATING]} ${copy[COPY_KEYS.HEADINGS.SITE].toLowerCase()}`
          : `${copy[COPY_KEYS.BUTTONS.CREATE]} ${copy[COPY_KEYS.COMMON.A]} ${copy[
              COPY_KEYS.HEADINGS.SITE
            ].toLowerCase()}`
      }
      trigger={update ? <EditButton /> : <CreateSiteButton />}
      footer={undefined}
      loading={loading}
      disabled={loading}
      open={open}
      setOpen={setOpen}
    >
      <InternalForm />
    </Dialog>
  )
}

export default CustomerSiteCreate
